/**
 * deed_home.ts — WHERE a versona resident of a PLAYER HOUSE lives inside it (PURE — no three, no DOM).
 *
 * A settlement home is furnished by `furnishVersonaHome`'s fixed layout, so `home_layout.interiorHomeSpot`
 * knows where the bed and the sofa are. A DEED's interior is whatever furnish pack its owner bought (or
 * nothing), so a resident takes a REAL piece of that plan: a sleeper a bed (a `lie` piece), a rester or a
 * chatter a seat (a `sit` piece), a child the floor beside the rug; with no such piece they stand on a
 * legal floor spot. Deterministic per (versona, building, state) so every client seats the same people the
 * same way; two residents may share a sofa or a bed — a family does. The lift is the piece's own height.
 */
import type { PlanSlot } from './interior_plans.js';
import type { HomeSpot, HomeSpecLike, ResidentRoom } from './home_layout.js';
import { homeHash01, standingSpot, anchoredHomeSpot } from './home_layout.js';
import { FURNITURE } from './furniture_catalog.js';
import { FURNITURE_FUNCTION } from './furniture_functions.js';
import { withoutLetUnits } from './rental_client.js'; // RENTALS (slice R, 2026-10-01): no resident in a tenant's room

/** The pack plan's slots by floor → the resident's spot on a real piece, or null when the plan has none for the state. */
export function deedHomeSpot(versonaId: string, buildingId: string, plan: ReadonlyArray<ReadonlyArray<PlanSlot>>, state: string): HomeSpot | null {
  const ha = homeHash01(`${versonaId}|${buildingId}|a`), hb = homeHash01(`${versonaId}|${buildingId}|b`);
  const want = state === 'sleeping' ? 'lie' : state === 'playing' ? 'rug' : 'sit';
  const cands: Array<{ slot: PlanSlot; floor: number }> = [];
  for (let f = 0; f < plan.length; f++) for (const slot of plan[f]) {
    const fn = FURNITURE_FUNCTION[slot.key] ?? null;
    if ((want === 'rug' && slot.key === 'rug_woven') || (want !== 'rug' && fn === want)) cands.push({ slot, floor: f });
  }
  if (cands.length === 0) return null;
  const { slot, floor } = cands[Math.floor(ha * cands.length) % cands.length];
  const h = FURNITURE[slot.key]?.h ?? 0.5;
  if (want === 'lie') return { lx: slot.x, lz: slot.z, floor, yaw: slot.yaw, lift: h * 0.7, lying: true };
  if (want === 'sit') return { lx: slot.x, lz: slot.z, floor, yaw: slot.yaw + Math.PI, lift: Math.min(0.5, h * 0.55), lying: false }; // a seat's front is its −Z: face the room like the player does
  const ang = hb * Math.PI * 2, r = 0.8 + ha * 0.8; // playing: a ring around the rug
  return { lx: slot.x + Math.cos(ang) * r, lz: slot.z + Math.sin(ang) * r, floor, yaw: ang + Math.PI, lift: 0, lying: false };
}

/** The resident's spot in a deed: a piece of the plan, else a standing spot on the floor. */
export function deedResidentSpot(versonaId: string, buildingId: string, plan: ReadonlyArray<ReadonlyArray<PlanSlot>>, spec: HomeSpecLike, state: string, room?: ResidentRoom | null): HomeSpot {
  // The planned room (slice C, 2026-10-01): the resident reads what the visit drew — the pack's seated pieces and the
  // planner's standing points. Without it (the lever, or the plan not landed) the legacy picker below, verbatim.
  // RENTALS (slice R): a let room is its tenant's — its bed, seats, rug and standing points are not on offer.
  if (room) return anchoredHomeSpot(versonaId, buildingId, withoutLetUnits(buildingId, room), spec, state);
  const spot = deedHomeSpot(versonaId, buildingId, plan, state);
  if (spot) return spot;
  const stand = standingSpot(versonaId, buildingId, spec);
  // A sleeper with no bed sleeps on a bedroll on the floor (home_layout's bedroll pad), never standing.
  return state === 'sleeping' ? { ...stand, lying: true, lift: 0.14 } : stand;
}
